import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faArrowRight } from '@fortawesome/pro-solid-svg-icons';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import Meal from './Meal';
import { useDispatch } from 'react-redux';
import { getSelectedCity } from '../../../../redux/selectors/summaryReducer.selectors';
import {
  mealExchangeCaloriesModalToggleStatus,
  mealExchangeRemoveMenuMeal,
  mealExchangeStoreCaloriesModalInfo,
  mealExchangeUpdateDayPriceAdjustment,
} from '../../../../redux/actions/orderFormActions';
import {
  addMenuMeal,
  displayAddMealButton,
  getLengthOfMealsPerDayByMealName,
  toggleMeal,
  validMeals,
} from '../helpers/mealExchangeHelpers';
import {
  getCurrentDiscountDetails,
  getMinMaxSelectedMeals,
  getSelectedDiet,
} from '../../../../redux/selectors/orderFormSelector';
import { faPlus, faSyncAlt, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { usePrevious, useWindowSize } from 'react-use';
import { customFind, parsePrice } from '../../../../utils';
import Button from '../../../common/Button';
import { getMealExchangeSinglePrice } from '../../../../utils/orderFormPrices';
import { getServerConfig } from '../../../../features/general/generalSlice';
import { getMenuConfigurationCaloriesSwitchEnabled } from '../../../../redux/selectors/formSettingsSelector';
import EmptyStateMealIcon from '../../../../assets/icons/EmptyStateMealIcon';
import { useGetApiFormSettingsQuery } from '../../../../services/api/form/open/open.api';
import { useAppSelector } from '../../../../config/hooks';

const CarouselMeals = ({
  activeDay,
  items,
  meals,
  mealsListPerDay,
  refs,
  sideOrdersPerDay,
}) => {
  const dispatch = useDispatch();
  const { sectorId } = useAppSelector(getSelectedCity);
  const { max, min } = useAppSelector(getMinMaxSelectedMeals);
  const { customDietOptions: areMealsSelectable } =
    useAppSelector(getSelectedDiet);
  const { apiKey, companyId, serverUrl } = useAppSelector(getServerConfig);

  const {
    data: {
      orderSettings: {
        addingMealsEnabled: isAddingMealsEnabled,
        coursePhotoVisibleInForm,
        customDietOptions: isShowMealsEnabled,
      } = {},
    } = {},
  } = useGetApiFormSettingsQuery(undefined, {
    skip: !companyId,
  });

  const currentDiscountDetails = useAppSelector(getCurrentDiscountDetails);
  const isMenuConfigurationCaloriesSwitchEnabled = useAppSelector(
    getMenuConfigurationCaloriesSwitchEnabled
  );
  const prevActiveDay = usePrevious(activeDay);

  const [swiper, setSwiper] = useState(null);
  const [isBeginningSlide, setIsBeginningSlide] = useState(true);
  const [isEndSlide, setIsEndSlide] = useState(false);

  const { width } = useWindowSize();

  const goNextSlide = () => {
    if (swiper) {
      swiper.slideNext();
    }
  };

  const goPrevSlide = () => {
    if (swiper) {
      swiper.slidePrev();
    }
  };

  const slidePerView = {
    0: {
      slidesPerView: 'auto',
      spaceBetween: 8,
    },
  };

  const {
    additionPriceInSectors,
    deductionPriceInSectors,
    id,
    list,
    mealId,
    mealName,
    selected,
  } = items;

  const additionPriceInSelectedSector =
    additionPriceInSectors && additionPriceInSectors[sectorId];
  const additionPrice = getMealExchangeSinglePrice(
    additionPriceInSelectedSector,
    currentDiscountDetails
  );

  const deductionPriceInSelectedSector =
    deductionPriceInSectors && deductionPriceInSectors[sectorId];
  const deductionPrice = getMealExchangeSinglePrice(
    deductionPriceInSelectedSector,
    currentDiscountDetails
  );
  const selectedMealVariant = list.find(mealVariant => mealVariant.selected);
  const selectedMealVariantPriceInSector = customFind(
    'sectorId',
    sectorId
  )(selectedMealVariant?.prices)?.additionPrice;

  useEffect(() => {
    if (swiper && prevActiveDay === activeDay) {
      swiper?.slideTo(
        list.findIndex(item => item.selected),
        400
      );
    }
  }, [activeDay, list, prevActiveDay, swiper]);

  const hideArrowButtons = () => {
    return (
      (list.length === 2 && width < 650) ||
      (list.length === 3 && width < 1024) ||
      (list.length === 4 && width < 1350) ||
      list.length > 4
    );
  };

  const ifDeleteButtonShouldBeVisible =
    (areMealsSelectable && isShowMealsEnabled) ||
    (!(areMealsSelectable && isShowMealsEnabled) &&
      isAddingMealsEnabled &&
      meals.length > 1);

  const { calories, menuMealName, otherCaloriesMenuMeals, prices } =
    selectedMealVariant || {};

  const hasSomePricesForOtherCalories = otherCaloriesMenuMeals?.some(
    meal => !!meal?.prices?.length
  );

  const handleChangeCaloriesClick = () => {
    const findMealPriceBySector = customFind(
      'sectorId',
      sectorId
    )(prices)?.additionPrice;

    dispatch(
      mealExchangeStoreCaloriesModalInfo(
        otherCaloriesMenuMeals,
        menuMealName,
        id,
        mealId,
        findMealPriceBySector
      )
    );
    dispatch(mealExchangeCaloriesModalToggleStatus());
  };

  const handleOnClickRemoveMeal = () => {
    if (
      dispatch(
        validMeals(mealsListPerDay, sideOrdersPerDay, min, max, 'minMeals')
      )
    ) {
      if (
        getLengthOfMealsPerDayByMealName(mealsListPerDay, items.mealName) > 1
      ) {
        dispatch(mealExchangeRemoveMenuMeal(id));
        dispatch(
          mealExchangeUpdateDayPriceAdjustment(
            -(
              additionPriceInSelectedSector +
              selectedMealVariantPriceInSector -
              additionPriceInSelectedSector
            ),
            'meals'
          )
        );
      } else {
        dispatch(
          toggleMeal(companyId, activeDay, id, items, 'off', serverUrl, apiKey)
        );
        dispatch(
          mealExchangeUpdateDayPriceAdjustment(
            -(
              deductionPriceInSelectedSector +
              selectedMealVariantPriceInSector -
              additionPriceInSelectedSector
            ),
            'meals'
          )
        );
      }
    }
  };

  if (!selected) {
    return (
      <div className="display-flex flex-direction-column align-items-top spacer-vertical-40">
        <h3 className="h300 display-flex align-items-center spacer-bottom-24">
          {mealName}
        </h3>
        <div className="carousel-meals__empty-state">
          <div className="display-flex align-items-center spacer-bottom-16">
            <EmptyStateMealIcon className="spacer-right-24 empty-state-meal-icon" />
            <p className="label-l color-gray-900">Brak dodanego posiłku</p>
          </div>
          <Button
            blockButton
            colorBlank
            icon={<FontAwesomeIcon className="font-size-16" icon={faPlus} />}
            label={`Dodaj posiłek za ${parsePrice(deductionPrice)} zł`}
            onClick={() => {
              if (
                dispatch(
                  validMeals(mealsListPerDay, sideOrdersPerDay, min, max, 'max')
                )
              ) {
                dispatch(
                  toggleMeal(
                    companyId,
                    activeDay,
                    id,
                    items,
                    'on',
                    serverUrl,
                    apiKey
                  )
                );
                dispatch(
                  mealExchangeUpdateDayPriceAdjustment(
                    deductionPriceInSelectedSector,
                    'meals'
                  )
                );
              }
            }}
            sizeMedium
          />
        </div>
      </div>
    );
  }

  return (
    <div className="carousel-meals">
      <div className="carousel-meals__top-wrapper">
        <div className="display-flex align-items-center">
          <h3>{mealName}</h3>
          <div className="hidden-down-sm">
            {ifDeleteButtonShouldBeVisible && (
              <div
                className="color-gray-500 cursor-pointer spacer-left-16"
                onClick={() => {
                  handleOnClickRemoveMeal();
                }}
                onKeyDown={() => {
                  handleOnClickRemoveMeal();
                }}
                role="button"
                tabIndex={0}
              >
                <FontAwesomeIcon className="font-size-16" icon={faTrash} />
              </div>
            )}
          </div>
        </div>
        <div className="hidden-down-sm">
          <span className="carousel-meals__top-wrapper-label">
            {calories} kcal
          </span>
          {isMenuConfigurationCaloriesSwitchEnabled &&
            hasSomePricesForOtherCalories && (
              <div
                className="label-s color-primary display-inline-block cursor-pointer"
                onClick={handleChangeCaloriesClick}
                onKeyDown={handleChangeCaloriesClick}
                role="button"
                tabIndex={0}
              >
                <FontAwesomeIcon className="spacer-right-4" icon={faSyncAlt} />
                Zmień kaloryczność
              </div>
            )}
        </div>
        <div className="display-flex align-items-center justify-content-space-between spacer-vertical-16 hidden-up-sm">
          <div className="display-flex align-items-center">
            <span className="carousel-meals__top-wrapper-label">
              {calories} kcal
            </span>
            {isMenuConfigurationCaloriesSwitchEnabled &&
              hasSomePricesForOtherCalories && (
                <div
                  className="label-s color-primary display-inline-block cursor-pointer"
                  onClick={handleChangeCaloriesClick}
                  onKeyDown={handleChangeCaloriesClick}
                  role="button"
                  tabIndex={0}
                >
                  <FontAwesomeIcon
                    className="spacer-right-4"
                    icon={faSyncAlt}
                  />
                  Zmień kaloryczność
                </div>
              )}
          </div>
          {ifDeleteButtonShouldBeVisible && (
            <div
              className="color-gray-500 cursor-pointer"
              onClick={() => {
                handleOnClickRemoveMeal();
              }}
              onKeyDown={() => {
                handleOnClickRemoveMeal();
              }}
              role="button"
              tabIndex={0}
            >
              <FontAwesomeIcon className="font-size-16" icon={faTrash} />
            </div>
          )}
        </div>
      </div>
      <div className="position-rel height-fit-content">
        {!isBeginningSlide && hideArrowButtons() && (
          <>
            <Button
              colorBlank
              containerClass="carousel-meals__slider-button carousel-meals__slider-button--left"
              icon={<FontAwesomeIcon icon={faArrowLeft} />}
              onClick={goPrevSlide}
            />
            <div className="carousel-meals__overlay carousel-meals__overlay--left" />
          </>
        )}
        <Swiper
          breakpoints={slidePerView}
          className="overflow-inherit"
          longSwipesRatio={0.2}
          observeParents
          observer
          observeSlideChildren
          onSlideChange={e => {
            setIsBeginningSlide(e.isBeginning);
            setIsEndSlide(e.isEnd);
          }}
          onSwiper={setSwiper}
          threshold={10}
        >
          {list.map(meal => (
            <SwiperSlide
              className="height-auto  width-auto"
              key={meal.dietCaloriesMealId}
              onClick={() => {
                if (swiper) {
                  swiper.slideToClickedSlide();
                }
              }}
            >
              <Meal
                coursePhotoVisibleInForm={coursePhotoVisibleInForm}
                id={id}
                meal={meal}
                mealId={mealId}
                refs={refs}
              />
            </SwiperSlide>
          ))}
        </Swiper>
        {!isEndSlide && hideArrowButtons() && (
          <>
            <Button
              colorBlank
              containerClass="carousel-meals__slider-button carousel-meals__slider-button--right"
              icon={<FontAwesomeIcon icon={faArrowRight} />}
              onClick={goNextSlide}
            />
            <div className="carousel-meals__overlay carousel-meals__overlay--right" />
          </>
        )}
      </div>
      {displayAddMealButton(mealsListPerDay, items, isAddingMealsEnabled) && (
        <div className="carousel-meals__add-button">
          <Button
            containerClass="spacer-top-24"
            icon={<FontAwesomeIcon className="font-size-16" icon={faPlus} />}
            label={`Dodaj posiłek za ${parsePrice(additionPrice)} zł`}
            onClick={() => {
              if (
                dispatch(
                  validMeals(mealsListPerDay, sideOrdersPerDay, min, max, 'max')
                )
              ) {
                dispatch(
                  addMenuMeal(companyId, activeDay, items, serverUrl, apiKey)
                );
                dispatch(
                  mealExchangeUpdateDayPriceAdjustment(
                    additionPriceInSelectedSector,
                    'meals'
                  )
                );
              }
            }}
          />
        </div>
      )}
    </div>
  );
};

CarouselMeals.propTypes = {
  activeDay: PropTypes.string,
  items: PropTypes.object,
  meals: PropTypes.array,
  mealsListPerDay: PropTypes.array,
  refs: PropTypes.object,
  sideOrdersPerDay: PropTypes.array,
};

export default CarouselMeals;
